import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Loader2, Play, Square, Terminal, Cpu, Calendar, Power, RefreshCw, Clock, CheckCircle2, AlertCircle, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { useToast } from "../../../contexts/ToastContext";
import { supabase } from "../../../lib/supabase";

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

export function RustProcessorControl() {
  const { showToast } = useToast();
  
  // States
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("2026-02-01");
  const [toDate, setToDate] = useState("2026-02-28");
  const [logs, setLogs] = useState<string[]>([]);
  const [ghRuns, setGhRuns] = useState<any[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [wsStatus, setWsStatus] = useState<"connected" | "disconnected" | "connecting">("disconnected");
  const [schedulerStatus, setSchedulerStatus] = useState<any>(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [selectedVmTarget, setSelectedVmTarget] = useState<number | 'ALL'>('ALL');

  const VM_TARGETS = [
    { index: 1, name: "VM1", hostName: "nickyansen-scheduler", desc: "Part 1 / Processor VM" },
    { index: 2, name: "VM2", hostName: "vm-ipot-scraper-v2", desc: "Part 2" },
    { index: 3, name: "VM3", hostName: "vm-ipot-scraper-v3", desc: "Part 3" },
  ];
  
  const terminalRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // Load Rust Direct Scheduler Status
  const loadSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${API_URL}/api/developer/rust/scheduler-status`, {
        method: 'GET',
        headers
      });

      if (!response.ok) throw new Error("Failed to load scheduler status");
      const resData = await response.json();
      setSchedulerStatus(resData.data || resData);
    } catch (error: any) {
      console.error("Error loading scheduler status:", error);
    } finally {
      setLoadingScheduler(false);
    }
  };

  // Load GitHub Workflow Runs
  const loadGhRuns = async () => {
    setLoadingRuns(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Get the Supabase auth token dynamically
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${API_URL}/api/developer/rust/gh-runs`, {
        method: 'GET',
        headers
      });

      if (!response.ok) throw new Error("Failed to load GitHub action runs");
      const resData = await response.json();
      if (resData.ok) {
        setGhRuns(resData.data);
      }
    } catch (error: any) {
      console.error("Error loading GitHub runs:", error);
    } finally {
      setLoadingRuns(false);
    }
  };

  // Run Backend Action
  const triggerAction = async (endpoint: string, actionName: string, body?: any) => {
    if (endpoint === "stop-vms") {
      const confirmed = window.confirm("Masih ada instance berjalan di VM, konfirmasi untuk deallocate?");
      if (!confirmed) return;
    }

    setLoadingAction(actionName);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Get the Supabase auth token dynamically
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${API_URL}/api/developer/rust/${endpoint}`, {
        method: 'POST',
        headers,
        body: body ? JSON.stringify(body) : undefined
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.message || `HTTP error ${response.status}`);
      }

      const resData = await response.json();
      if (resData.ok) {
        showToast({
          type: "success",
          title: "Success",
          message: resData.data?.message || `${actionName} triggered successfully`
        });
        // Reload runs in case we triggered a workflow
        setTimeout(loadGhRuns, 3000);
      } else {
        throw new Error(resData.message || "Request failed");
      }
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to trigger action"
      });
    } finally {
      setLoadingAction(null);
    }
  };

  // Connect to WebSocket for Rust processor logs
  useEffect(() => {
    const connectWs = () => {
      setWsStatus("connecting");
      const hostname = window.location.hostname || 'localhost';
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${hostname}:9001`;
      
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
      } catch (err: any) {
        console.error("Failed to connect to Rust WebSocket:", err);
        setWsStatus("disconnected");
        setLogs(prev => [...prev, `[System Error] Failed to connect to Rust WebSocket at ${wsUrl}: ${err.message || err}`]);
        // Retry connection after 10 seconds
        setTimeout(connectWs, 10000);
        return;
      }

      ws.onopen = () => {
        setWsStatus("connected");
        setLogs(prev => [...prev, `[System] Connected to Rust WebSocket server at ${wsUrl}`]);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const timestamp = new Date().toLocaleTimeString();
          if (data.msg) {
            setLogs(prev => [...prev, `[${timestamp}] ${data.msg}`]);
          } else {
            setLogs(prev => [...prev, `[${timestamp}] ${event.data}`]);
          }
        } catch (_) {
          const timestamp = new Date().toLocaleTimeString();
          setLogs(prev => [...prev, `[${timestamp}] ${event.data}`]);
        }
      };

      ws.onclose = () => {
        setWsStatus("disconnected");
        setLogs(prev => [...prev, "[System] Disconnected from Rust WebSocket. Retrying in 10s..."]);
        setTimeout(connectWs, 10000);
      };

      ws.onerror = () => {
        setWsStatus("disconnected");
      };
    };

    connectWs();

    // Load initial GH actions runs and Direct Scheduler status
    loadGhRuns();
    loadSchedulerStatus();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Auto-scroll logs terminal container only (without scrolling the viewport)
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [logs]);

  const refreshAll = () => {
    loadGhRuns();
    loadSchedulerStatus();
  };

  const schedState = schedulerStatus?.state || schedulerStatus;
  const isSchedOnline = schedulerStatus?.online !== false && schedState?.current_wib_time && !schedState?.current_wib_time.includes("Offline");

  return (
    <Card className="w-full border-zinc-200 dark:border-zinc-800">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-xl font-bold">
            <Cpu className="h-5 w-5 text-indigo-500" />
            Rust Processor & Direct Azure Scheduler
          </CardTitle>
          <CardDescription>
            Direct Azure control (No GitHub Actions) & automated daily pipelines (16:10, 18:40, 18:52, 20:00 WIB)
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isSchedOnline ? "default" : "secondary"}>
            Scheduler: {isSchedOnline ? "ONLINE (8085)" : "OFFLINE"}
          </Badge>
          <Badge variant={wsStatus === "connected" ? "default" : wsStatus === "connecting" ? "secondary" : "destructive"}>
            WS: {wsStatus.toUpperCase()}
          </Badge>
          <Button onClick={refreshAll} size="sm" variant="outline" title="Refresh runs and scheduler">
            <RefreshCw className={`h-4 w-4 ${(loadingRuns || loadingScheduler) ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Direct Azure Scheduler Card */}
        <div className="p-4 border rounded-xl bg-gradient-to-r from-indigo-50/40 via-purple-50/20 to-blue-50/30 dark:from-indigo-950/20 dark:via-purple-950/10 dark:to-blue-950/20 border-indigo-200/60 dark:border-indigo-800/40 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                Direct Azure Pipeline (Tanpa GitHub Action)
              </span>
              <Badge variant="outline" className="text-xs bg-white/80 dark:bg-zinc-900/80">
                <Clock className="h-3 w-3 mr-1 text-indigo-500" />
                {schedState?.current_wib_time || 'Syncing clock...'}
              </Badge>
            </div>
            {schedState?.last_status_message && (
              <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate max-w-md">
                Status: {schedState.last_status_message}
              </span>
            )}
          </div>

          {/* Schedule timetable badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">16:10 WIB (Scraper)</span>
              <Badge variant={schedState?.scrapers_run_today ? "default" : "secondary"} className="text-[10px]">
                {schedState?.scrapers_run_today ? "Done" : "Pending"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">&lt; 18:45 WIB (Taskkill)</span>
              <div className="flex items-center gap-1.5">
                <Button
                  onClick={() => triggerAction("kill-scraper", "Taskkill Scrapers (All VMs)")}
                  disabled={loadingAction !== null}
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
                  title="Quick Taskkill All VMs"
                >
                  <Square className="h-2.5 w-2.5 mr-1" /> Kill
                </Button>
                <Badge variant={schedState?.kill_run_today ? "default" : "secondary"} className="text-[10px]">
                  {schedState?.kill_run_today ? "Done" : "Pending"}
                </Badge>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">18:52 WIB (Processor)</span>
              <Badge variant={schedState?.processor_run_today ? "default" : "secondary"} className="text-[10px]">
                {schedState?.processor_run_today ? "Done" : "Pending"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">20:00 WIB (Deallocate)</span>
              <Badge variant={schedState?.deallocate_run_today ? "default" : "secondary"} className="text-[10px]">
                {schedState?.deallocate_run_today ? "Done" : "Pending"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">21:00 WIB (Python)</span>
              <Badge variant={schedState?.python_scrapers_run_today ? "default" : "secondary"} className="text-[10px]">
                {schedState?.python_scrapers_run_today ? "Done" : "Pending"}
              </Badge>
            </div>
          </div>

          {/* VM Target Quick Filter Selector */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-indigo-100 dark:border-indigo-900/40 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">Target VM Default:</span>
              <div className="inline-flex rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => setSelectedVmTarget('ALL')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    selectedVmTarget === 'ALL'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                  }`}
                >
                  Semua VM (All)
                </button>
                {VM_TARGETS.map(vm => (
                  <button
                    key={vm.index}
                    type="button"
                    onClick={() => setSelectedVmTarget(vm.index)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      selectedVmTarget === vm.index
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                    }`}
                    title={`${vm.name} (${vm.hostName})`}
                  >
                    {vm.name}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-[11px] text-zinc-500 italic">
              {selectedVmTarget === 'ALL'
                ? 'Semua tombol otomatis mengeksekusi VM1, VM2, VM3 secara paralel. Klik tanda panah ▼ pada tiap button untuk target individual.'
                : `Target aktif: VM${selectedVmTarget} (${VM_TARGETS.find(v => v.index === selectedVmTarget)?.hostName}). Klik tanda panah ▼ untuk memilih VM lain.`}
            </span>
          </div>

          {/* Direct Trigger Action Buttons with VM Menus on EVERY button */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* 1. Split Button: Run IPOT Scrapers (All VMs / VM1 / VM2 / VM3) */}
            <div className="inline-flex items-center rounded-md border border-emerald-600 dark:border-emerald-700 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm overflow-hidden">
              <Button
                onClick={() => {
                  if (selectedVmTarget === 'ALL') {
                    triggerAction("direct-scraper", "Direct Scrapers (All VMs)");
                  } else {
                    triggerAction("direct-scraper", `Direct Scraper VM${selectedVmTarget}`, { vm_index: selectedVmTarget });
                  }
                }}
                disabled={loadingAction !== null}
                className="bg-transparent hover:bg-emerald-700/80 text-white font-medium text-xs h-8 px-2.5 rounded-none shadow-none"
                size="sm"
                title={`Jalankan Scraper IPOT pada ${selectedVmTarget === 'ALL' ? 'Semua VM (VM1, VM2, VM3)' : `VM${selectedVmTarget}`}`}
              >
                {loadingAction?.includes("Direct Scraper") ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                )}
                Run IPOT Scrappers {selectedVmTarget === 'ALL' ? '(VM1, VM2, VM3)' : `(VM${selectedVmTarget})`}
              </Button>
              <div className="h-4 w-[1px] bg-emerald-500/80" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={loadingAction !== null}
                    className="bg-transparent hover:bg-emerald-700/80 text-white text-xs h-8 px-1.5 rounded-none shadow-none"
                    size="sm"
                    title="Pilih Target VM untuk Scraper"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 text-xs">
                  <DropdownMenuLabel className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                    Pilih Target Run Scraper
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => triggerAction("direct-scraper", "Direct Scrapers (All VMs)")}
                    className="cursor-pointer font-medium text-emerald-600 dark:text-emerald-400 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      <Play className="h-3.5 w-3.5" /> Run All (VM1, VM2, VM3)
                    </span>
                    <Badge variant="default" className="text-[9px] py-0 px-1 bg-emerald-600">All</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {VM_TARGETS.map(vm => (
                    <DropdownMenuItem
                      key={vm.index}
                      onClick={() => triggerAction("direct-scraper", `Direct Scraper VM${vm.index}`, { vm_index: vm.index })}
                      className="cursor-pointer flex items-center justify-between"
                    >
                      <span className="flex items-center gap-1.5">
                        <Play className="h-3 w-3 text-emerald-500" />
                        {vm.name} ({vm.hostName})
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1">{vm.name}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 2. Split Button: Taskkill IPOT Scrapers (All VMs / VM1 / VM2 / VM3) */}
            <div className="inline-flex items-center rounded-md border border-red-300 dark:border-red-800/80 bg-white/50 dark:bg-zinc-900/50 shadow-sm overflow-hidden">
              <Button
                onClick={() => {
                  if (selectedVmTarget === 'ALL') {
                    triggerAction("kill-scraper", "Taskkill Scrapers (All VMs)");
                  } else {
                    triggerAction("kill-scraper", `Taskkill VM${selectedVmTarget}`, { vm_index: selectedVmTarget });
                  }
                }}
                disabled={loadingAction !== null}
                variant="ghost"
                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs h-8 px-2.5 rounded-none font-medium"
                size="sm"
                title={`Taskkill IPOT dan Python pada ${selectedVmTarget === 'ALL' ? 'Semua VM (VM1, VM2, VM3)' : `VM${selectedVmTarget}`}`}
              >
                {loadingAction?.includes("Taskkill") ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Square className="h-3.5 w-3.5 mr-1.5" />
                )}
                Taskkill IPOT Scrapers {selectedVmTarget === 'ALL' ? '(All VMs)' : `(VM${selectedVmTarget})`}
              </Button>
              <div className="h-4 w-[1px] bg-red-200 dark:bg-red-800/80" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={loadingAction !== null}
                    variant="ghost"
                    className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs h-8 px-1.5 rounded-none"
                    size="sm"
                    title="Pilih Target VM untuk Taskkill"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 text-xs">
                  <DropdownMenuLabel className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                    Pilih Target Taskkill
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => triggerAction("kill-scraper", "Taskkill Scrapers (All VMs)")}
                    className="cursor-pointer font-medium text-red-600 dark:text-red-400 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      <Square className="h-3.5 w-3.5" /> Taskkill All (VM1, VM2, VM3)
                    </span>
                    <Badge variant="destructive" className="text-[9px] py-0 px-1">All</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {VM_TARGETS.map(vm => (
                    <DropdownMenuItem
                      key={vm.index}
                      onClick={() => triggerAction("kill-scraper", `Taskkill VM${vm.index}`, { vm_index: vm.index })}
                      className="cursor-pointer flex items-center justify-between"
                    >
                      <span className="flex items-center gap-1.5">
                        <Square className="h-3 w-3 text-red-500" />
                        {vm.name} ({vm.hostName})
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1">{vm.name}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 3. Split Button: Run scheduler_main_daily (VM1) */}
            <div className="inline-flex items-center rounded-md border border-indigo-600 dark:border-indigo-700 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm overflow-hidden">
              <Button
                onClick={() => triggerAction("direct-processor", "Direct Processor (VM1)")}
                disabled={loadingAction !== null}
                className="bg-transparent hover:bg-indigo-700/80 text-white font-medium text-xs h-8 px-3 rounded-none shadow-none"
                size="sm"
                title="Jalankan Rust Processor scheduler_main_daily pada VM1"
              >
                {loadingAction === "Direct Processor (VM1)" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                )}
                Run scheduler_main_daily (VM1)
              </Button>
              <div className="h-4 w-[1px] bg-indigo-500/80" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={loadingAction !== null}
                    className="bg-transparent hover:bg-indigo-700/80 text-white text-xs h-8 px-1.5 rounded-none shadow-none"
                    size="sm"
                    title="Menu Target Processor"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 text-xs">
                  <DropdownMenuLabel className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                    Target Processor Rust
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => triggerAction("direct-processor", "Direct Processor (VM1)")}
                    className="cursor-pointer font-medium text-indigo-600 dark:text-indigo-400 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      <Play className="h-3.5 w-3.5" /> VM1 (nickyansen-scheduler)
                    </span>
                    <Badge variant="default" className="text-[9px] py-0 px-1 bg-indigo-600">Host</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                    scheduler_main_daily dikompilasi &amp; berjalan pada VM1 sebagai master processor.
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 4. Split Button: Start VMs Direct (All VMs / VM1 / VM2 / VM3) */}
            <div className="inline-flex items-center rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-sm overflow-hidden">
              <Button
                onClick={() => {
                  if (selectedVmTarget === 'ALL') {
                    triggerAction("direct-start-vms", "Direct Start VMs (All)");
                  } else {
                    triggerAction("direct-start-vms", `Direct Start VM${selectedVmTarget}`, { vm_index: selectedVmTarget });
                  }
                }}
                disabled={loadingAction !== null}
                variant="ghost"
                className="text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/50 text-xs h-8 px-2.5 rounded-none font-medium"
                size="sm"
                title={`Nyalakan Azure VM ${selectedVmTarget === 'ALL' ? 'Semua (VM1, VM2, VM3)' : `VM${selectedVmTarget}`}`}
              >
                {loadingAction?.includes("Direct Start VM") ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Power className="h-3.5 w-3.5 mr-1.5 text-emerald-500" />
                )}
                Start VMs Direct {selectedVmTarget === 'ALL' ? '(All VMs)' : `(VM${selectedVmTarget})`}
              </Button>
              <div className="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-700" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={loadingAction !== null}
                    variant="ghost"
                    className="text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/50 text-xs h-8 px-1.5 rounded-none"
                    size="sm"
                    title="Pilih VM untuk Dinyalakan"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 text-xs">
                  <DropdownMenuLabel className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                    Pilih Target Start VM
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => triggerAction("direct-start-vms", "Direct Start VMs (All)")}
                    className="cursor-pointer font-medium text-emerald-600 dark:text-emerald-400 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      <Power className="h-3.5 w-3.5" /> Start All (VM1, VM2, VM3)
                    </span>
                    <Badge variant="outline" className="text-[9px] py-0 px-1 border-emerald-500 text-emerald-600">All</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {VM_TARGETS.map(vm => (
                    <DropdownMenuItem
                      key={vm.index}
                      onClick={() => triggerAction("direct-start-vms", `Direct Start VM${vm.index}`, { vm_index: vm.index })}
                      className="cursor-pointer flex items-center justify-between"
                    >
                      <span className="flex items-center gap-1.5">
                        <Power className="h-3 w-3 text-emerald-500" />
                        {vm.name} ({vm.hostName})
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1">{vm.name}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 5. Split Button: Stop VMs Direct (All VMs / VM1 / VM2 / VM3) */}
            <div className="inline-flex items-center rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-sm overflow-hidden">
              <Button
                onClick={() => {
                  if (selectedVmTarget === 'ALL') {
                    triggerAction("direct-stop-vms", "Direct Stop VMs (All)");
                  } else {
                    triggerAction("direct-stop-vms", `Direct Stop VM${selectedVmTarget}`, { vm_index: selectedVmTarget });
                  }
                }}
                disabled={loadingAction !== null}
                variant="ghost"
                className="text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/50 text-xs h-8 px-2.5 rounded-none font-medium"
                size="sm"
                title={`Deallocate / Matikan VM ${selectedVmTarget === 'ALL' ? 'Semua (VM1, VM2, VM3)' : `VM${selectedVmTarget}`}`}
              >
                {loadingAction?.includes("Direct Stop VM") ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Square className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                )}
                Stop VMs Direct {selectedVmTarget === 'ALL' ? '(All VMs)' : `(VM${selectedVmTarget})`}
              </Button>
              <div className="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-700" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={loadingAction !== null}
                    variant="ghost"
                    className="text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/50 text-xs h-8 px-1.5 rounded-none"
                    size="sm"
                    title="Pilih VM untuk Deallocate"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 text-xs">
                  <DropdownMenuLabel className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                    Pilih Target Stop / Deallocate
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => triggerAction("direct-stop-vms", "Direct Stop VMs (All)")}
                    className="cursor-pointer font-medium text-red-600 dark:text-red-400 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      <Square className="h-3.5 w-3.5" /> Stop All (VM1, VM2, VM3)
                    </span>
                    <Badge variant="destructive" className="text-[9px] py-0 px-1">All</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {VM_TARGETS.map(vm => (
                    <DropdownMenuItem
                      key={vm.index}
                      onClick={() => triggerAction("direct-stop-vms", `Direct Stop VM${vm.index}`, { vm_index: vm.index })}
                      className="cursor-pointer flex items-center justify-between"
                    >
                      <span className="flex items-center gap-1.5">
                        <Square className="h-3 w-3 text-red-500" />
                        {vm.name} ({vm.hostName})
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1">{vm.name}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 6. Direct CI/CD Deploy VM1 (No GitHub Actions) */}
            <Button
              onClick={() => triggerAction("direct-deploy-vm1", "Direct CI/CD Deploy (VM1)")}
              disabled={loadingAction !== null}
              variant="outline"
              className="border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-xs h-8 px-2.5 font-medium shadow-sm"
              size="sm"
              title="Pull origin/main, compile processor & scheduler, dan restart service di VM1 langsung (tanpa GitHub Actions)"
            >
              {loadingAction === "Direct CI/CD Deploy (VM1)" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 text-indigo-500" />
              )}
              Deploy VM1 Direct (No GH Action)
            </Button>
          </div>
        </div>

        {/* Legacy / GitHub Actions Fallback Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 p-3 border rounded-lg bg-zinc-50/50 dark:bg-zinc-900/50">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Azure VMs</span>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => triggerAction("start-vms", "Start VMs")} 
                disabled={loadingAction !== null} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                size="sm"
              >
                {loadingAction === "Start VMs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4 mr-1.5" />}
                Start VMs
              </Button>
              <Button 
                onClick={() => triggerAction("stop-vms", "Stop VMs")} 
                disabled={loadingAction !== null} 
                variant="destructive"
                size="sm"
                className="w-full"
              >
                {loadingAction === "Stop VMs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4 mr-1.5" />}
                Stop VMs
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2 p-3 border rounded-lg bg-zinc-50/50 dark:bg-zinc-900/50">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Daily Tasks</span>
            <Button 
              onClick={() => triggerAction("run-scraper-all", "Run Scraper All")} 
              disabled={loadingAction !== null}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              size="sm"
            >
              {loadingAction === "Run Scraper All" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Run IPOT Scrapper (All)
            </Button>
            <Button 
              onClick={() => triggerAction("run-processor", "Run Processor")} 
              disabled={loadingAction !== null}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
            >
              {loadingAction === "Run Processor" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
              Run Rust Processor
            </Button>
          </div>

          <div className="flex flex-col gap-2 p-3 border rounded-lg bg-zinc-50/50 dark:bg-zinc-900/50">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Distributed Backlog Scraper</span>
            <div className="flex gap-2">
              <input 
                type="date" 
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-1/2 px-2 py-1 text-xs border rounded focus:outline-indigo-500 bg-transparent text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-800 [color-scheme:light] dark:[color-scheme:dark]"
              />
              <input 
                type="date" 
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-1/2 px-2 py-1 text-xs border rounded focus:outline-indigo-500 bg-transparent text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-800 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
            <Button 
              onClick={() => triggerAction("run-scraper-batch", "Run Scraper Batch", { from: fromDate, to: toDate })} 
              disabled={loadingAction !== null || !fromDate || !toDate}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              size="sm"
            >
              {loadingAction === "Run Scraper Batch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4 mr-1.5" />}
              Run Backlog (Split VMs)
            </Button>
            <Button 
              onClick={() => triggerAction("run-processor-date", "Run Processor Date", { from: fromDate, to: toDate })} 
              disabled={loadingAction !== null || !fromDate || !toDate}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
            >
              {loadingAction === "Run Processor Date" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
              Run Processor (Batch)
            </Button>
          </div>
        </div>

        {/* Live Terminal & Workflow Monitor */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* WebSockets Logs Terminal */}
          <div className="lg:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300">
                <Terminal className="h-4 w-4" />
                Live Processor Output (WebSocket)
              </span>
              <Button onClick={() => setLogs([])} variant="ghost" size="xs" className="text-xs h-7 px-2">
                Clear
              </Button>
            </div>
            <div ref={terminalRef} className="h-64 bg-zinc-950 text-zinc-50 font-mono text-xs p-3 rounded-lg overflow-y-auto border border-zinc-900 shadow-inner">
              {logs.length === 0 ? (
                <span className="text-zinc-500">Idle. Waiting for Rust logs...</span>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className="leading-5 whitespace-pre-wrap break-all">
                    {log}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* GitHub Actions Workflows monitor */}
          <div className="space-y-2">
            <span className="text-sm font-semibold flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300">
              <RefreshCw className="h-4 w-4" />
              GitHub Actions Runs (Workflow Dispatch)
            </span>
            <div className="h-64 border rounded-lg overflow-y-auto bg-zinc-50/30 dark:bg-zinc-950/20 divide-y divide-zinc-100 dark:divide-zinc-800">
              {loadingRuns && ghRuns.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-zinc-500">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading runs...
                </div>
              ) : ghRuns.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-zinc-500">
                  No workflow runs found
                </div>
              ) : (
                ghRuns.map((run: any) => (
                  <div key={run.id} className="p-3 text-xs space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold truncate max-w-[150px]">{run.name}</span>
                      <Badge variant={run.status === "completed" ? (run.conclusion === "success" ? "default" : "destructive") : "secondary"}>
                        {run.status === "completed" ? run.conclusion : run.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-zinc-500">
                      <span>Run #{run.run_number}</span>
                      <span>{new Date(run.created_at).toLocaleTimeString()}</span>
                    </div>
                    {run.html_url && (
                      <a 
                        href={run.html_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-indigo-600 dark:text-indigo-400 hover:underline block"
                      >
                        View Logs on GitHub ↗
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
